"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

declare const wvcClient: {
  getCurrentWPTermQueryParams: () => Record<string, any>;
};

function ShopBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || links.length === 0) return null;

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList className="justify-center text-xs uppercase tracking-widest text-muted-foreground">
        {links.map((link, index) => (
          <BreadcrumbItem
            key={link.href}
            data-index={index}
            className="text-xs"
          >
            {index === lastIndex ? (
              <BreadcrumbPage className="text-foreground">
                {link.title}
              </BreadcrumbPage>
            ) : (
              <>
                <BreadcrumbLink
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {link.title}
                </BreadcrumbLink>
                <BreadcrumbSeparator />
              </>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleConsumer() {
  const { terms, loading, error } = useEcommerceTerms();

  if (loading) {
    return (
      <div data-section-id="17604" className="mx-auto mt-8 h-12 w-72 animate-pulse bg-muted/40 md:h-16 md:w-96" />
    );
  }

  if (error || terms.length === 0) {
    return (
      <h1 className="font-default text-4xl font-light tracking-tight text-foreground md:text-6xl lg:text-7xl">
        Travel Destinations
      </h1>
    );
  }

  const term = terms[0];

  return (
    <div className="flex flex-col items-center">
      <span className="mb-4 text-xs uppercase tracking-[0.3em] text-accent-foreground">
        Innoleaf Adventures
      </span>
      <h1 className="font-default text-4xl font-light tracking-tight text-foreground md:text-6xl lg:text-7xl">
        {term.name}
      </h1>
      {term.description && (
        <p
          className="mt-6 max-w-2xl text-center text-base leading-relaxed text-muted-foreground md:text-lg"
          dangerouslySetInnerHTML={{ __html: term.description }}
        />
      )}
      <div className="mt-8 h-px w-16 bg-border" />
    </div>
  );
}

export default function () {
  const current_wp_term_query_params = wvcClient.getCurrentWPTermQueryParams();
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="archive-title"
      className="relative bg-background text-foreground px-4 pt-16 pb-12 md:pt-24 md:pb-16 lg:pt-32 lg:pb-20"
    >
      <div className="mx-auto flex max-w-5xl flex-col items-center">
        <div className="mb-10 md:mb-12">
          <ShopBreadcrumbsProvider>
            <ShopBreadcrumbsConsumer />
          </ShopBreadcrumbsProvider>
        </div>

        <EcommerceTermsProvider
          className="w-full text-center"
          wp_term_query={wp_term_query}
        >
          <ArchiveTitleConsumer />
        </EcommerceTermsProvider>
      </div>
    </section>
  );
}